<template>
  <section class="parcel-add-wrapper">
    <!-- Alert: No item found -->
    <b-alert
      variant="danger"
      :show="parcelData === undefined"
    >
      <h4 class="alert-heading">
        {{ $t('Error fetching parcel data') }}
      </h4>
      <div class="alert-body">
        No parcel found with this parcel id. Check
        <b-link
          class="alert-link"
          :to="{ name: 'ship2u-parcel-list'}"
        >
          Parcel List
        </b-link>
        for other parcels.
      </div>
    </b-alert>

    <b-row
      v-if="parcelData"
      class="parcel-add"
    >

      <!-- Col: Left (Parcel Container) -->
      <b-col
        cols="12"
        xl="9"
        md="8"
      >
        <b-form @submit.prevent>
          <b-card
            no-body
            class="parcel-preview-card"
          >
            <!-- Header -->
            <div v-if="!parcelData.receive_time">
              <b-card-body>
                <b-card-title>{{ $t('Forecast Information') }}</b-card-title>
                <b-card-sub-title>{{ $t('This is the forecast information, the final data is subject to the warehouse information.') }}</b-card-sub-title>
              </b-card-body>
              <b-card-body class="parcel-padding pb-0">
                <b-row>
                  <b-col cols="12">
                    <b-form-group
                      :label="`${$t('title')}`"
                      label-for="h-first-name"
                      label-cols-md="4"
                    >
                      <validation-provider
                        #default="{ errors }"
                        :name="$t('title')"
                        rules="required"
                      >
                        <b-input-group>
                          <template #prepend>
                            <b-input-group-text><strong class="text-danger">*</strong></b-input-group-text>
                          </template>
                          <b-form-input
                            id="title"
                            v-model="parcelData.title"
                            :state="errors.length > 0 ? false:null"
                            :placeholder="$t('Using exact name of content')"
                          />
                        </b-input-group>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12">
                    <b-form-group
                      :label="$t('Transportation')"
                      label-cols-md="4"
                    >
                      <b-input-group>
                        <template #prepend>
                          <b-input-group-text><strong class="text-danger">*</strong></b-input-group-text>
                        </template>
                        <v-select
                          v-model="freightType"
                          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                          :options="ftypes"
                          label="title"
                        >
                          <template #option="{ title, icon }">
                            <feather-icon
                              :icon="icon"
                              size="16"
                              class="align-middle mr-50"
                            />
                            <span> {{ title }}</span>
                          </template>
                        </v-select>
                      </b-input-group>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12">
                    <b-form-group
                      :label="$t('Local Courier Number')"
                      label-for="h-email"
                      label-cols-md="4"
                    >
                      <validation-provider
                        #default="{ errors }"
                        :name="$t('Local Courier Number')"
                        rules="required"
                      >
                        <b-input-group>
                          <template #prepend>
                            <b-input-group-text><strong class="text-danger">*</strong></b-input-group-text>
                          </template>
                          <b-form-input
                            id="lcno"
                            v-model="parcelData.local_courier_number"
                            :state="errors.length > 0 ? false:null"
                            placeholder="Normal Input"
                          />
                        </b-input-group>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12">
                    <b-form-group
                      :label="$t('Local Courier Name')"
                      label-for="h-first-name"
                      label-cols-md="4"
                    >
                      <b-form-input
                        id="lcname"
                        v-model="parcelData.local_courier_name"
                        placeholder="Normal Input"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12">
                    <b-form-group
                      label="Expected Receiving Date"
                      label-for="h-email"
                      label-cols-md="4"
                    >
                      <flat-pickr
                        v-model="parcelData.signin_time"
                        class="form-control"
                        :config="{ enableTime: true,dateFormat: 'Y-m-d H:i'}"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12">
                    <b-form-group
                      :label="$t('Number of Items')"
                      label-for="h-first-name"
                      label-cols-md="4"
                    >
                      <b-input-group :append="$t('Pieces')">
                        <b-form-input
                          id="pieces"
                          v-model="parcelData.pieces"
                          placeholder="Normal Input"
                        />
                      </b-input-group>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12">
                    <b-form-group
                      :label="$t('Volume')"
                      label-for="h-email"
                      label-cols-md="4"
                    >
                      <b-input-group append="M3">
                        <b-form-input
                          id="volume"
                          v-model="parcelData.volume"
                          placeholder="Normal Input"
                        />
                      </b-input-group>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12">
                    <b-form-group
                      :label="$t('Weight')"
                      label-for="h-first-name"
                      label-cols-md="4"
                    >
                      <b-input-group append="KG">
                        <b-form-input
                          id="weight"
                          v-model="parcelData.weight"
                          placeholder="Normal Input"
                        />
                      </b-input-group>
                    </b-form-group>
                  </b-col>

                </b-row>
              </b-card-body>
            </div>
            <div v-else>
              <b-card-body>
                <b-card-title>{{ $t('Parcel Information') }}</b-card-title>
                <b-card-sub-title>{{ $t('Please contact us if the parcel information is not correct.') }}</b-card-sub-title>
              </b-card-body>
              <b-card-body class="parcel-padding pb-0">
                <div
                  class="d-flex justify-content-between flex-md-row flex-column parcel-spacing mt-0"
                >
                  <!-- Header: Left Content -->
                  <div>
                    <div class="logo-wrapper">
                      <h3 class="text-primary parcel-logo">
                        {{ parcelData.title }}
                      </h3>
                    </div>
                    <div class="parcel-date-wrapper">
                      <p class="parcel-date-title">
                        {{ $t('Transportation') }}:
                      </p>
                      <p class="parcel-date">
                        {{ freightType.title }}
                      </p>
                    </div>
                    <div class="parcel-date-wrapper">
                      <p class="parcel-date-title">
                        {{ $t('Local Courier Name') }}:
                      </p>
                      <p class="parcel-date">
                        {{ parcelData.local_courier_name }}
                      </p>
                    </div>

                    <div class="parcel-date-wrapper">
                      <p class="parcel-date-title">
                        {{ $t('Local Courier Number') }}:
                      </p>
                      <p class="parcel-date">
                        {{ parcelData.local_courier_number }}
                      </p>
                    </div>

                    <div class="parcel-date-wrapper">
                      <p class="parcel-date-title">
                        {{ $t('Number of Items') }}:
                      </p>
                      <p class="parcel-date">
                        {{ parcelData.pieces }} {{ $t('Pieces') }}
                      </p>
                    </div>

                    <div class="parcel-date-wrapper">
                      <p class="parcel-date-title">
                        {{ $t('Volume') }}:
                      </p>
                      <p class="parcel-date">
                        {{ parcelData.volume.toFixed(3) }} M3
                      </p>
                    </div>

                    <div class="parcel-date-wrapper">
                      <p class="parcel-date-title">
                        {{ $t('Weight') }}:
                      </p>
                      <p class="parcel-date">
                        {{ parcelData.weight }} KG
                      </p>
                    </div>

                    <div class="parcel-date-wrapper">
                      <p class="parcel-date-title">
                        {{ $t('Material Volume') }}:
                      </p>
                      <p class="parcel-date">
                        {{ $VolWeightFlight(parcelData.volume) }} KG
                      </p>
                    </div>
                  </div>

                  <!-- Header: Right Content -->
                  <div class="parcel-number-date mt-md-0 mt-2">
                    <div class="d-flex align-items-center justify-content-md-end mb-1">
                      <h4 class="parcel-title">
                        {{ $t('Parcel') }}
                      </h4>
                      <b-input-group class="input-group-merge parcel-edit-input-group disabled">
                        <b-input-group-prepend is-text>
                          <feather-icon icon="HashIcon" />
                        </b-input-group-prepend>
                        <b-form-input
                          id="parcel-data-id"
                          v-model="parcelData.id"
                          disabled
                        />
                      </b-input-group>
                    </div>
                    <div class="d-flex align-items-center mb-1">
                      <br><br><br>
                      <p class="title">
                        {{ $t('Expected Date') }}:
                      </p>
                      <p class="parcel-date">
                        {{ $DateFormat(parcelData.signin_time) }}
                      </p>
                    </div>
                    <div class="d-flex align-items-center mb-1">
                      <br><br><br>
                      <p class="title">
                        {{ $t('Date Received') }}:
                      </p>
                      <p class="parcel-date">
                        {{ $DateFormat(parcelData.receive_time) }}
                      </p>
                    </div>
                  </div>
                </div>
              </b-card-body>
            </div>
            <!-- Spacer -->
            <hr class="parcel-spacing">
            <!-- Items Section -->
            <b-card no-body>
              <b-card-body>
                <b-card-title>{{ $t('Clearance Items') }}</b-card-title>
                <b-card-sub-title>{{ $t('Clearance Items Info') }}</b-card-sub-title>
              </b-card-body>
              <b-card-body class="parcel-padding form-item-section">
                <div
                  ref="form"
                  class="repeater-form"
                  :style="{height: trHeight}"
                >
                  <b-row
                    v-for="(item, index) in parcelData.orderdetail_set"
                    :key="index"
                    ref="row"
                    class="pb-2"
                  >
                    <!-- Item Form -->
                    <!-- ? This will be in loop => So consider below markup for single item -->
                    <b-col cols="12">

                      <!-- ? Flex to keep separate width for XIcon and SettingsIcon -->
                      <div class="d-none d-lg-flex">
                        <b-row class="flex-grow-1 px-1">
                          <!-- Single Item Form Headers -->
                          <b-col
                            cols="12"
                            lg="3"
                          >
                            {{ $t('Item Name') }}
                          </b-col>
                          <b-col
                            cols="12"
                            lg="3"
                          >
                            {{ $t('Material') }}
                          </b-col>
                          <b-col
                            cols="12"
                            lg="2"
                          >
                            {{ $t('Unit Price') }}
                          </b-col>
                          <b-col
                            cols="12"
                            lg="2"
                          >
                            {{ $t('Pieces') }}
                          </b-col>
                          <b-col
                            cols="12"
                            lg="2"
                          >
                            {{ $t('Total Price') }}
                          </b-col>
                        </b-row>
                        <div class="form-item-action-col" />
                      </div>

                      <!-- Form Input Fields OR content inside bordered area  -->
                      <!-- ? Flex to keep separate width for XIcon and SettingsIcon -->
                      <div class="d-flex border rounded">
                        <b-row class="flex-grow-1 p-2">
                          <!-- Single Item Form Headers -->
                          <b-col
                            cols="12"
                            lg="3"
                          >
                            <b-form-input
                              v-model="item.title"
                              autocomplete="true"
                              list="titles"
                            />
                            <datalist id="titles">
                              <option
                                v-for="(title, titleIndex) in datasuggest.titles.data"
                                :key="titleIndex"
                              >
                                {{ title }}
                              </option>
                            </datalist>
                          </b-col>
                          <b-col
                            cols="12"
                            lg="3"
                          >
                            <b-form-input
                              v-model="item.reference"
                              autocomplete="true"
                              list="materials"
                            />
                            <datalist id="materials">
                              <option
                                v-for="(material, materialIndex) in datasuggest.materials.data"
                                :key="materialIndex"
                              >
                                {{ material }}
                              </option>
                            </datalist>
                          </b-col>
                          <b-col
                            cols="12"
                            lg="2"
                          >
                            <b-form-input
                              v-model="item.price"
                              type="number"
                              class="mb-2"
                              min="0.01"
                              step="0.01"
                              @update="priceUpdate(index)"
                            />
                          </b-col>
                          <b-col
                            cols="12"
                            lg="2"
                          >
                            <b-form-input
                              v-model="item.quantity"
                              type="number"
                              class="mb-2"
                              min="1"
                              step="1"
                              @update="quantityUpdate(index)"
                            />
                          </b-col>
                          <b-col
                            cols="12"
                            lg="2"
                          >
                            <b-form-input
                              v-model="item.subtotal"
                              type="number"
                              min="0.01"
                              step="0.01"
                              class="mb-2"
                              @update="subtotalUpdate(index)"
                            />
                          </b-col>
                        </b-row>
                        <div class="d-flex flex-column justify-content-between border-left py-50 px-25">
                          <feather-icon
                            size="16"
                            icon="XIcon"
                            class="cursor-pointer"
                            @click="removeItem(index)"
                          />
                        </div>
                      </div>
                    </b-col>
                  </b-row>
                </div>
                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  size="sm"
                  variant="primary"
                  @click="addNewItemInItemForm"
                >
                  {{ $t('Add Item') }}
                </b-button>
              </b-card-body>
            </b-card>

            <!-- Parcel Description: Total -->
            <b-card-body class="parcel-padding pb-0">
              <b-row>

                <!-- Col: Sales Persion -->
                <b-col
                  cols="12"
                  md="6"
                  class="mt-md-0 mt-3"
                  order="2"
                  order-md="1"
                />

                <!-- Col: Total -->
                <b-col
                  cols="12"
                  md="6"
                  class="mt-md-6 d-flex justify-content-end"
                  order="1"
                  order-md="2"
                >
                  <div class="parcel-total-wrapper">
                    <hr class="my-50">
                    <div class="parcel-total-item">
                      <p class="parcel-total-title">
                        {{ $t('Total') }}:
                      </p>
                      <p class="parcel-total-amount">
                        {{ totalPrice() }}
                      </p>
                    </div>
                  </div>
                </b-col>
              </b-row>
            </b-card-body>

            <!-- Spacer -->
            <hr class="parcel-spacing">

            <!-- Note -->
            <b-card-body class="parcel-padding pt-0">
              <span class="font-weight-bold">{{ $t('Customer Note') }}: </span>
              <b-form-textarea v-model="parcelData.customer_note" />
            </b-card-body>
          </b-card>
        </b-form>
      </b-col>

      <!-- Right Col: Card -->
      <b-col
        cols="12"
        md="4"
        xl="3"
        class="parcel-actions"
      >

        <!-- Action Buttons -->
        <b-card>

          <b-button
            v-ripple.400="'rgba(113, 102, 240, 0.15)'"
            variant="primary"
            block
            :disabled="pressed"
            @click="onPostParcel"
          >
            {{ $t('Save') }}
          </b-button>

          <!-- Button: DOwnload -->
          <b-button
            v-ripple.400="'rgba(113, 102, 240, 0.15)'"
            variant="outline-primary"
            class="mb-75"
            block
            :to="{ name: 'ship2u-parcel-preview', params: { id: $route.params.id } }"
          >
            {{ $t('Preview') }}
          </b-button>

        </b-card>
      </b-col>
    </b-row>
  </section>
</template>

<script>
/* eslint-disable global-require */
// import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
// import { VueAutosuggest } from 'vue-autosuggest'
import { ValidationProvider } from 'vee-validate'
import { required } from '@validations'
import { $themeConfig } from '@themeConfig'
import { ref } from '@vue/composition-api'
import { heightTransition } from '@core/mixins/ui/transition'
import Ripple from 'vue-ripple-directive'
import flatPickr from 'vue-flatpickr-component'
import vSelect from 'vue-select'
import store from '@/store'
import {
  BRow, BCol, BCard, BCardTitle, BCardSubTitle, BCardBody, BButton, BForm, BFormGroup, BFormInput, BInputGroup, BInputGroupText, BInputGroupPrepend, BFormTextarea, BAlert, BLink, VBToggle,
} from 'bootstrap-vue'

export default {
  components: {
    // BCardActions,
    // VueAutosuggest,
    flatPickr,
    BRow,
    BCol,
    BCard,
    BCardTitle,
    BCardSubTitle,
    BCardBody,
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupText,
    BInputGroupPrepend,
    BFormTextarea,
    BAlert,
    BLink,
    ValidationProvider,
    vSelect,
  },
  directives: {
    Ripple,
    'b-toggle': VBToggle,
  },
  mixins: [heightTransition],
  data() {
    const datasuggest = { titles: [{ data: [] }], materials: [{ data: [] }] }
    // App Name
    const { appLogoImage } = $themeConfig.app
    const itemFormBlankItem = {
      id: 0,
      title: null,
      reference: '',
      price: 0,
      quantity: 0,
      subtotal: 0,
    }
    const parcelData = ref(null)

    return {
      pressed: false,
      freightType: null,
      datasuggest,
      required,
      parcelData,
      itemFormBlankItem,
      appLogoImage,
      ftypes: [
        {
          title: 'Ocean Shipping',
          icon: 'ApertureIcon',
          code: 'S',
        },
        {
          title: 'Air Transport',
          icon: 'CodepenIcon',
          code: 'F',
        },
      ],
    }
  },
  // Reset Tr Height if data changes
  watch: {
    // eslint-disable-next-line func-names
    'parcelData.orderdetail_set': function () {
      // eslint-disable-next-line prefer-destructuring
      this.freightType = this.ftypes.filter(item => item.code === this.parcelData.freight_type)[0]
      this.initTrHeight()
    },
  },
  mounted() {
    this.initTrHeight()
    store.dispatch('ship2u/fetchParcel', { id: this.$router.currentRoute.params.id })
      .then(response => {
        this.parcelData = response
        if (this.parcelData.orderdetail_set.length < 1) {
          this.parcelData.orderdetail_set.push(JSON.parse(JSON.stringify(this.itemFormBlankItem)))
        }
      })
      .catch(error => {
        if (error.response.status === 404) {
          this.parcelData = undefined
        }
      })
    store.dispatch('ship2u/parcelNames')
      .then(res => {
        console.log('names', res)
        if (res.obj) {
          this.datasuggest.titles.data = []
          this.datasuggest.materials.data = []
          res.obj.titles.forEach(i => {
            this.datasuggest.titles.data.push(i.title)
          })
          res.obj.materials.forEach(i => {
            this.datasuggest.materials.data.push(i.material)
          })
        }
      })
  },
  created() {
    window.addEventListener('resize', this.initTrHeight)
  },
  destroyed() {
    window.removeEventListener('resize', this.initTrHeight)
  },
  methods: {
    formData() {
      const data = {
        customer_add: true,
        id: this.parcelData.id,
        title: this.parcelData.title,
        local_courier_number: this.parcelData.local_courier_number,
        local_courier_name: this.parcelData.local_courier_name,
        freight_type: this.freightType.code,
        signin_time: this.parcelData.signin_time,
        pieces: this.parcelData.pieces,
        volume: this.parcelData.volume,
        weight: this.parcelData.weight,
        customer_note: this.parcelData.customer_note,
        titles: [],
        prices: [],
        quantities: [],
        totals: [],
        materials: [],
        detail_ids: [],
      }
      this.parcelData.orderdetail_set.forEach(i => {
        data.titles.push(i.title)
        data.prices.push(i.price)
        data.quantities.push(i.quantity)
        data.materials.push(i.reference)
        data.totals.push(i.subtotal)
      })
      data.titles = JSON.stringify(data.titles)
      data.prices = JSON.stringify(data.prices)
      data.quantities = JSON.stringify(data.quantities)
      data.materials = JSON.stringify(data.materials)
      data.totals = JSON.stringify(data.totals)
      console.log('Upload', data)
      return data
    },
    totalPrice() {
      let total = 0
      if (this.parcelData.orderdetail_set !== undefined) {
        this.parcelData.orderdetail_set.forEach(i => {
          total += (parseFloat(i.subtotal) || 0)
        })
      }
      return total.toFixed(2)
    },
    quantityUpdate(i) {
      console.log('quantity', this.parcelData.orderdetail_set)
      console.log('index', i)
      this.parcelData.orderdetail_set[i].subtotal = this.parcelData.orderdetail_set[i].price * this.parcelData.orderdetail_set[i].quantity
    },
    subtotalUpdate(i) {
      console.log('subtotal', this.parcelData.orderdetail_set)
      this.parcelData.orderdetail_set[i].price = (this.parcelData.orderdetail_set[i].subtotal / this.parcelData.orderdetail_set[i].quantity).toFixed(2)
    },
    priceUpdate(i) {
      console.log('price', this.parcelData.orderdetail_set)
      this.parcelData.orderdetail_set[i].subtotal = this.parcelData.orderdetail_set[i].price * this.parcelData.orderdetail_set[i].quantity
    },
    addNewItemInItemForm() {
      this.$refs.form.style.overflow = 'hidden'
      this.parcelData.orderdetail_set.push(JSON.parse(JSON.stringify(this.itemFormBlankItem)))

      this.$nextTick(() => {
        this.trAddHeight(this.$refs.row[0].offsetHeight)
        setTimeout(() => {
          this.$refs.form.style.overflow = null
        }, 350)
      })
    },
    onPostParcel() {
      this.pressed = true
      const data = this.formData()
      store.dispatch('ship2u/saveParcel', { obj: data, parcel: this.parcelData })
        .then(response => {
          console.log(response)
          // parcelData = response
          // parcelDescription.value = response.orderdetail_set
          this.$bvModal.msgBoxOk(this.$i18n.t('Data was submitted successfully'), {
            title: this.$i18n.t('Confirmation'),
            // size: 'sm',
            // buttonSize: 'sm',
            okTitle: this.$i18n.t('OK'),
            okVariant: 'success',
            headerClass: 'p-2 border-bottom-0',
            footerClass: 'p-2 border-top-0',
            // centered: true,
          })
            .then(() => {
              this.$router.go(-1)
              this.pressed = false
            })
            .catch(err => {
            // An error occurred
              console.log(err)
            })
        })
        .catch(error => {
          if (error.response.status === 404) {
            this.parcelData = undefined
          }
        })
      // editParcel(formData.value)
    },
    removeItem(i) {
      this.parcelData.orderdetail_set.splice(i, 1)
      this.trTrimHeight(this.$refs.row[0].offsetHeight)
    },
    initTrHeight() {
      this.trSetHeight(null)
      this.$nextTick(() => {
        this.trSetHeight(this.$refs.form ? this.$refs.form.scrollHeight : 0)
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>

<style lang="scss" scoped>
@import "~@core/scss/base/pages/app-parcel.scss";

.form-item-section {
background-color:$product-details-bg;
}

.form-item-action-col {
  width: 27px;
}

.repeater-form {
  // overflow: hidden;
  transition: .35s height;
}

.v-select {
  &.item-selector-title,
  &.payment-selector {
    background-color: #fff;

    .dark-layout & {
      background-color: unset;
    }
  }
}

.dark-layout {
  .form-item-section {
    background-color: $theme-dark-body-bg;

    .row .border {
      background-color: $theme-dark-card-bg;
    }

  }
}
</style>
<style lang="scss">
@import '@core/scss/vue/libs/vue-autosuggest.scss';
</style>
